/**
 * Every /creatives read (spec §2.2), each under its own run key through D1's useKeyedRead:
 * - facts: the scope, the four dates and the attempt (R13), never the token: 'mtd' and an equal custom range read once;
 * - the Result definition: the workspace alone (D1's loadMapping, which never rejects); none in all workspaces, where
 *   the central definition applies;
 * - freshness: the workspace alone (D1's loadChannelFreshness); none in all workspaces, so the pill falls back to the
 *   facts (R1).
 * One attempt counter sits in every key: retry() re-reads everything once. A null scope reads nothing.
 *
 * No filter is an input: channel, objective, currency, format, sort and "แสดงทั้งหมด" are applied by
 * buildCreativeView on the client and read nothing.
 *
 * @module hooks/useCreativeData
 */
import { useCallback, useMemo, useState } from 'react';
import { creativeWindowsKey, creativeWindowsOf, type CreativeWindows } from '@/services/creatives/creativeWindows';
import { readCreativeFacts } from '@/services/creatives/creativeReaders';
import type { CreativeFact } from '@/services/creatives/creativeTypes';
import { loadChannelFreshness } from '@/services/looker/lookerAppLoaders';
import type { FreshChannel } from '@/services/looker/lookerAppData';
import { bangkokToday } from '@/services/looker/lookerDateRange';
import { loadMapping } from '@/services/overview/overviewLoaders';
import type { OverviewWindows, ReadState } from '@/services/overview/overviewTypes';
import { overviewWindowsOf } from '@/services/overview/overviewWindows';
import { CENTRAL_MAPPING, FAILED_MAPPING, type ResultMappingInfo } from '@/services/targets/resultMetric';
import type { ChannelFreshnessState } from './useLookerChannelFreshness';
import type { OverviewScope } from './useOverviewData';
import { useKeyedRead } from './useKeyedRead';

export interface CreativeData {
  /**
   * null = no bounded range (an empty "this month", a refused custom range): the facts are not read; the definition
   * and the freshness still are.
   */
  windows: OverviewWindows | null;
  creativeWindows: CreativeWindows | null;
  facts: ReadState<CreativeFact[]>;
  /** null while it loads (R12); the central definition in all workspaces; FAILED_MAPPING if the read failed. */
  mapping: ResultMappingInfo | null;
  /** idle in all workspaces. */
  freshness: ChannelFreshnessState;
  /** Re-reads everything once. */
  retry: () => void;
}

const unscoped = (): Promise<never> => Promise.reject(new Error('creatives: no scope'));

const mappingOf = (scope: OverviewScope | null, read: ReadState<ResultMappingInfo>): ResultMappingInfo | null => {
  if (!scope) return null;
  if (scope.workspaceId === null) return CENTRAL_MAPPING;
  if (read.status === 'ready') return read.data;
  // loadMapping never rejects; a failure here is the read itself, and a stuck table would be worse than "—".
  return read.status === 'failed' ? FAILED_MAPPING : null;
};

const freshnessOf = (read: ReadState<FreshChannel[]>): ChannelFreshnessState => {
  if (read.status === 'ready') return { status: 'ready', channels: read.data };
  return read.status === 'failed' ? { status: 'failed' } : read.status === 'loading' ? { status: 'loading' } : { status: 'idle' };
};

export const useCreativeData = (scope: OverviewScope | null, token: string): CreativeData => {
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  const windows = useMemo(() => overviewWindowsOf(token, bangkokToday()), [token]);
  const creativeWindows = useMemo(() => (windows ? creativeWindowsOf(windows) : null), [windows]);
  const ids = scope?.workspaceIds ?? [];
  const ws = scope?.workspaceId ?? null;
  const at = `a:${attempt}`;
  const factsKey = scope && creativeWindows
    ? `facts|${ws ?? 'all'}|${ids.join(',')}|${creativeWindowsKey(creativeWindows)}|${at}` : '';
  const wsKey = scope && ws ? `${ws}|${at}` : '';

  const facts = useKeyedRead<CreativeFact[]>(factsKey,
    () => (creativeWindows ? readCreativeFacts(ids, creativeWindows) : unscoped()), 'CreativeFacts');
  const mappingRead = useKeyedRead<ResultMappingInfo>(wsKey && `map|${wsKey}`, () => (ws ? loadMapping(ws) : unscoped()), 'CreativeMapping');
  const freshnessRead = useKeyedRead<FreshChannel[]>(wsKey && `fr|${wsKey}`,
    () => (ws ? loadChannelFreshness(ws) : unscoped()), 'CreativeFreshness');
  const mapping = mappingOf(scope, mappingRead);
  const freshness = useMemo(() => freshnessOf(freshnessRead), [freshnessRead]);

  return useMemo(
    () => ({ windows, creativeWindows, facts, mapping, freshness, retry }),
    [windows, creativeWindows, facts, mapping, freshness, retry],
  );
};
